import BigNumber from 'bignumber.js';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  OrderExchangeFundingData,
  OrderExchangeFundingMode,
  OrderFundingBlock,
  SelectedAccount,
  TradeType,
  ValidatedOrder,
} from '../../types';
import { isDefined, Loadable, LoadableState } from '../../utils';
import { OpenOrder } from '../open-orders';
import {
  useOrderCostCalculationCommon,
  UseOrderCostCalculationCommonPlatformParams,
} from '../order/useOrderCostCalculationCommon';
import {
  useOrderExchangeBalanceForCurrencyCommon,
  UseOrderExchangeBalanceForCurrencyCommonPlatformParams,
} from './useOrderExchangeBalanceForCurrencyCommon';

type UseOrderExchangeFundingCommonParams = {
  order: ValidatedOrder | undefined;
  selectedAccount: SelectedAccount | undefined;
  isExchangeEnabled: boolean;
  openOrder: OpenOrder | undefined;
};

type UseOrderExchangeFundingOutput = {
  funding: LoadableState<OrderExchangeFundingData>;
  fundingMode: OrderExchangeFundingMode | undefined;
  setFundingMode: (mode: OrderExchangeFundingMode) => void;
  loadFunding: () => Promise<void>;
  reset: () => void;
};

export type UseOrderExchangeFundingCommonPlatformParams = UseOrderExchangeBalanceForCurrencyCommonPlatformParams &
  UseOrderCostCalculationCommonPlatformParams;

export const useOrderExchangeFundingCommon = (
  { order, selectedAccount, isExchangeEnabled, openOrder }: UseOrderExchangeFundingCommonParams,
  {
    accountResource,
    orderManagementResource,
    getResourceHeaderWithTokenForAccount,
  }: UseOrderExchangeFundingCommonPlatformParams,
): UseOrderExchangeFundingOutput => {
  const { calculateOrderCost } = useOrderCostCalculationCommon({
    getResourceHeaderWithTokenForAccount,
    orderManagementResource,
  });
  const { balanceForCurrency } = useOrderExchangeBalanceForCurrencyCommon({
    accountResource,
    getResourceHeaderWithTokenForAccount,
  });
  const [funding, setFunding] = useState<LoadableState<OrderExchangeFundingData>>(Loadable.uninitialized);
  const [manualFundingMode, setManualFundingMode] = useState<OrderExchangeFundingMode | undefined>(undefined);
  const requestIdRef = useRef(0);

  const fundingMode = useMemo((): OrderExchangeFundingMode | undefined => {
    if (!isExchangeEnabled) {
      return undefined;
    }
    if (Loadable.hasValue(funding) && funding.missingAmount === 0) {
      return 'fullAmount';
    }
    if (isDefined(manualFundingMode)) {
      return manualFundingMode;
    }
    if (!Loadable.hasValue(funding)) {
      return undefined;
    }
    return 'missingAmount';
  }, [isExchangeEnabled, manualFundingMode, funding]);

  // Performs an update if no subsequent requests have been started since the update started.
  const updateFunding = useCallback((requestId: number, value: LoadableState<OrderExchangeFundingData>): boolean => {
    if (requestId !== requestIdRef.current) {
      return false;
    }
    setFunding(value);
    return true;
  }, []);

  const loadFunding = useCallback(async (): Promise<void> => {
    const requestId = ++requestIdRef.current;
    try {
      if (
        !isDefined(selectedAccount?.account?.accountNumber) ||
        !isDefined(order) ||
        !isDefined(order.price) ||
        !balanceForCurrency.isLoaded ||
        order.tradeType !== TradeType.Buy
      ) {
        updateFunding(requestId, Loadable.uninitialized);
        return;
      }

      const accountId = selectedAccount.account.accountNumber;
      if (!updateFunding(requestId, Loadable.loading)) {
        return;
      }

      const fullCalculatedCost = await calculateOrderCost(order, accountId);

      // Minimum investable amount is 0.
      // If the user has a negative balance it's irrelevant - we don't want to pay their balance together with an order.
      const balanceInTargetCurrency = Math.max(balanceForCurrency.value ?? 0, 0);
      const totalBlock = calculateTotalBlock(order, fullCalculatedCost.buyAmount);
      const paidBlock = calculatePaidBlock(openOrder);
      const remainingBlock = calculateRemainingBlock(totalBlock, paidBlock);
      const newFundingData: OrderExchangeFundingData = {
        paid: paidBlock,
        remaining: remainingBlock,
        total: totalBlock,
        missingAmount: calculateMissingAmount(remainingBlock, balanceInTargetCurrency),
        targetCurrency: order.instrument.currency,
        balanceInTargetCurrency,
      };
      updateFunding(requestId, Loadable.loaded(newFundingData));
    } catch (_error) {
      console.error(_error);
      updateFunding(requestId, Loadable.failed);
    }
  }, [
    selectedAccount?.account?.accountNumber,
    order,
    openOrder,
    updateFunding,
    calculateOrderCost,
    balanceForCurrency,
  ]);

  const reset = useCallback((): void => {
    ++requestIdRef.current;
    setFunding(Loadable.uninitialized);
    setManualFundingMode(undefined);
  }, []);

  return {
    funding,
    fundingMode,
    setFundingMode: setManualFundingMode,
    loadFunding,
    reset,
  };
};

const calculateTotalBlock = (order: ValidatedOrder, commission: number): OrderFundingBlock => {
  const orderValue = new BigNumber(order.price ?? 0).multipliedBy(order.quantity);
  const total = new BigNumber(orderValue).plus(commission);
  return {
    commission,
    orderValue: orderValue.toNumber(),
    total: total.toNumber(),
  };
};

const calculatePaidBlock = (openOrder: OpenOrder | undefined): OrderFundingBlock => {
  if (!isDefined(openOrder)) {
    return { orderValue: 0, commission: 0, total: 0 };
  }
  const orderValue = new BigNumber(openOrder.price ?? 0).multipliedBy(openOrder.quantity);
  const commission = openOrder.commission ?? 0;
  const total = new BigNumber(orderValue).plus(commission);
  return {
    commission,
    orderValue: orderValue.toNumber(),
    total: total.toNumber(),
  };
};

const calculateRemainingBlock = (totalBlock: OrderFundingBlock, paidBlock: OrderFundingBlock): OrderFundingBlock => {
  const orderValue = BigNumber.max(new BigNumber(totalBlock.orderValue).minus(paidBlock.orderValue), 0);
  const commission = BigNumber.max(new BigNumber(totalBlock.commission).minus(paidBlock.commission), 0);
  const total = BigNumber.max(new BigNumber(totalBlock.total).minus(paidBlock.total), 0);
  return {
    commission: commission.toNumber(),
    orderValue: orderValue.toNumber(),
    total: total.toNumber(),
  };
};

const calculateMissingAmount = (remainingBlock: OrderFundingBlock, balance: number): number => {
  return BigNumber.max(new BigNumber(remainingBlock.total).minus(balance), 0).toNumber();
};
